// frontend/src/features/seo/components/ContentBriefsTab.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import EditContentBriefModal from './EditContentBriefModal';

const TONE_OPTIONS = ['Friendly', 'Professional', 'Authoritative', 'Conversational', 'Technical'];

const FORMAT_OPTIONS = [
  'Blog Post \u2014 Long-form article with...',
  'How-To Guide \u2014 Step-by-step tutorial',
  'Case Study \u2014 Results and metrics focused',
  'Product Comparison \u2014 In-depth breakdown',
  'Thought Leadership \u2014 Strategic perspective',
];

export default function ContentBriefsTab({
  briefs = [],
  onAddBrief,
  onApproveBrief,
  onOpenCreateBrief,
  onOpenInStudio,
  onUpdateBrief,
  onDeleteBrief,
  onClearBriefs,
  onEnhanceWithAI,
  onAuditDraft,
  onOpenSchema,
  onNavigateTab,
  isEnhancingBrief = false,
  isGeneratingBrief = false,
  attentionBriefId = null,
  onClearAttention,
}) {
  const [selectedBrief, setSelectedBrief] = useState(briefs[0] || null);
  const [topicInput, setTopicInput] = useState(briefs[0]?.title || '');
  const [selectedTone, setSelectedTone] = useState('Friendly');
  const [selectedFormat, setSelectedFormat] = useState(FORMAT_OPTIONS[0]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [copiedOutline, setCopiedOutline] = useState(false);
  const [expandedOutlineIndex, setExpandedOutlineIndex] = useState(null);

  // Fallback modal states for Create New Content Brief
  const [isInternalCreateBriefOpen, setIsInternalCreateBriefOpen] = useState(false);
  const [internalBriefTitle, setInternalBriefTitle] = useState('');
  const [internalBriefSlug, setInternalBriefSlug] = useState('');
  const [internalBriefAudience, setInternalBriefAudience] = useState('');

  const handleOpenCreateBriefModal = (initialTitle = '') => {
    const trimmed = typeof initialTitle === 'string' ? initialTitle.trim() : '';
    if (onOpenCreateBrief) {
      onOpenCreateBrief(trimmed);
    } else {
      setInternalBriefTitle(trimmed);
      setInternalBriefSlug(
        trimmed
          ? `/blog/${trimmed
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '')}`
          : ''
      );
      setIsInternalCreateBriefOpen(true);
    }
  };

  const handleTopicInputClick = () => {
    const trimmed = (topicInput || '').trim();
    if (!trimmed) {
      handleOpenCreateBriefModal('');
      return;
    }

    const isExisting = briefs.some(
      (b) => (b.title || '').trim().toLowerCase() === trimmed.toLowerCase()
    );

    if (!isExisting) {
      handleOpenCreateBriefModal(trimmed);
    } else {
      const found = briefs.find(
        (b) => (b.title || '').trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (found && (!selectedBrief || selectedBrief.id !== found.id)) {
        handleSelectBrief(found);
      }
    }
  };

  const handleSaveInternalNewBrief = (e, mode = 'manual') => {
    if (e && e.preventDefault) e.preventDefault();
    if (!internalBriefTitle.trim()) return;

    const titleToUse = internalBriefTitle.trim();
    const slugToUse = internalBriefSlug
      ? internalBriefSlug.startsWith('/')
        ? internalBriefSlug.trim()
        : `/${internalBriefSlug.trim()}`
      : `/blog/${titleToUse
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')}`;
    const audienceToUse = internalBriefAudience.trim() || 'B2B Growth Leaders';

    const newCreatedBrief = {
      id: `brief-${Date.now()}`,
      title: titleToUse,
      targetSlug: slugToUse,
      targetAudience: audienceToUse,
      searchIntent: 'informational',
      conversionObjective: 'Sign up for Trial',
      targetWordCount: 1800,
      status: 'draft',
      approvedBy: null,
      approvedAt: null,
      outline: [
        { level: 'H1', text: titleToUse },
        { level: 'H2', text: 'Introduction & Core Definitions' },
        { level: 'H2', text: 'Strategic Blueprint' },
        { level: 'H2', text: 'Summary & Key Takeaways' },
      ],
      targetEntities: ['B2B Sales', 'Lead Generation'],
      proofPoints: ['Directly validated against SERP research data'],
      internalLinks: [{ page: '/features', anchorText: 'features' }],
      cta: 'Get started today',
      isAIGenerated: mode === 'ai',
    };

    if (onAddBrief) {
      onAddBrief(newCreatedBrief);
    } else if (onUpdateBrief) {
      onUpdateBrief(newCreatedBrief);
    }
    setSelectedBrief(newCreatedBrief);
    setTopicInput(newCreatedBrief.title);
    setIsInternalCreateBriefOpen(false);
    setInternalBriefTitle('');
    setInternalBriefSlug('');
    setInternalBriefAudience('');
  };

  const prevBriefsRef = useRef(briefs);

  // Sync selectedBrief with incoming briefs without overwriting free-form user typing
  useEffect(() => {
    if (briefs !== prevBriefsRef.current) {
      const prevLength = prevBriefsRef.current?.length || 0;
      prevBriefsRef.current = briefs;

      if (briefs.length > 0) {
        if (selectedBrief) {
          const found = briefs.find((b) => b.id === selectedBrief.id);
          if (found) {
            setSelectedBrief(found);
            return;
          }
        }
        // If a new brief was added (e.g. from topic research or creation), auto-select it
        if (briefs.length > prevLength || !selectedBrief) {
          setSelectedBrief(briefs[0]);
          setTopicInput(briefs[0].title || '');
        }
      } else {
        setSelectedBrief(null);
      }
    }
  }, [briefs, selectedBrief]);

  // When attentionBriefId is passed (e.g. from Content Studio unapproved publish redirect), auto-select that brief
  useEffect(() => {
    if (attentionBriefId && briefs.length > 0) {
      const matched = briefs.find((b) => b.id === attentionBriefId);
      if (matched) {
        setSelectedBrief(matched);
        setTopicInput(matched.title || '');
      }
    }
  }, [attentionBriefId, briefs]);

  const filteredBriefs = useMemo(() => {
    return briefs.filter((brief) => {
      const matchesStatus = statusFilter === 'all' || brief.status === statusFilter;
      const matchesSearch =
        !searchFilter ||
        brief.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
        brief.targetSlug.toLowerCase().includes(searchFilter.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [briefs, statusFilter, searchFilter]);

  const handleSaveEdit = (updatedBrief) => {
    setSelectedBrief(updatedBrief);
    setTopicInput(updatedBrief.title || '');
    if (onUpdateBrief) {
      onUpdateBrief(updatedBrief);
    }
  };

  const handleSelectBrief = (brief) => {
    setSelectedBrief(brief);
    setTopicInput(brief.title || '');
    if (brief.searchIntent) {
      if (brief.searchIntent.toLowerCase().includes('informational')) {
        setSelectedTone('Friendly');
      } else if (brief.searchIntent.toLowerCase().includes('commercial')) {
        setSelectedTone('Professional');
      }
    }
  };

  const handleGenerateContentClick = () => {
    if (selectedBrief && onEnhanceWithAI) {
      onEnhanceWithAI(selectedBrief);
    } else {
      handleTopicInputClick();
    }
  };

  const handleCopyOutline = () => {
    if (!selectedBrief?.outline) return;
    const text = selectedBrief.outline.map((item) => `${item.level}: ${item.text}`).join('\n');
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopiedOutline(true);
    setTimeout(() => setCopiedOutline(false), 2000);
  };

  const outlineItems = selectedBrief?.outline || [];
  const proofPoints = selectedBrief?.proofPoints || [];
  const internalLinks = selectedBrief?.internalLinks || [];

  return (
    <div className="seo-tab-container seo-onepage-studio-root">
      {isGeneratingBrief && (
        <div className="seo-generating-banner">
          <i className="fas fa-circle-notch fa-spin text-blue"></i>
          <span>
            <strong>Synthesizing AI Content Brief with Gemini:</strong> Analyzing SERP intent,
            entities, questions, and competitor gaps...
          </span>
        </div>
      )}

      {/* ── Main Single-Page Canvas ── */}
      <div className="seo-onepage-content-canvas">
        {/* ── TOP SECTION: Generate AIEO-optimized content Card ── */}
        <div className="seo-generator-card">
          {/* Top Bar on Generator Card: Title left, New Brief right */}
          <div className="seo-generator-card-top-bar">
            <div className="seo-generator-card-title-wrap">
              <h2 className="seo-section-title">
                <i className="fas fa-file-alt"></i> Content Briefs Studio
              </h2>
            </div>

            <div className="seo-tab-actions">
              <button
                type="button"
                className="seo-btn seo-btn-primary"
                onClick={onOpenCreateBrief}
                disabled={isGeneratingBrief}
              >
                {isGeneratingBrief ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Synthesizing Brief...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus"></i>
                    <span>New Brief</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="seo-generator-header">
            <h1 className="seo-generator-title">Generate AIEO-optimized content</h1>
            <p className="seo-generator-subtitle">
              Enter a topic and we'll build a structured outline, gather verified proof points,
              suggest internal links, and craft your primary call to action.
            </p>
          </div>

          <div className="seo-generator-form">
            <div className="seo-form-group">
              {/* Label row above topic input with right-aligned Workspace Briefs drawer button */}
              <div className="seo-topic-label-bar">
                <label htmlFor="seo-topic-input" className="seo-form-label">
                  What would you like to write about?
                </label>
                <button
                  type="button"
                  className="seo-btn-open-drawer"
                  onClick={() => setIsDrawerOpen(true)}
                  title="Open workspace briefs drawer"
                >
                  <i className="fas fa-folder-open"></i>
                  <span>Workspace Briefs ({filteredBriefs.length})</span>
                </button>
              </div>

              <div className="seo-topic-input-wrap">
                <input
                  id="seo-topic-input"
                  type="text"
                  className="seo-topic-input"
                  placeholder="e.g., Content marketing automation"
                  value={topicInput}
                  onClick={handleTopicInputClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTopicInputClick();
                    }
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTopicInput(val);
                    const matched = briefs.find(
                      (b) => (b.title || '').trim().toLowerCase() === val.trim().toLowerCase()
                    );
                    if (matched) {
                      setSelectedBrief(matched);
                    } else if (
                      selectedBrief &&
                      val.trim().toLowerCase() !== (selectedBrief.title || '').trim().toLowerCase()
                    ) {
                      setSelectedBrief(null);
                    }
                  }}
                />
                {topicInput && (
                  <button
                    type="button"
                    className="seo-input-clear-btn"
                    onClick={() => {
                      setTopicInput('');
                      setSelectedBrief(null);
                    }}
                    title="Clear input"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Tone and Format Dropdowns */}
            <div className="seo-generator-options-grid">
              <div className="seo-form-group">
                <label htmlFor="seo-tone-select" className="seo-form-label">
                  Tone
                </label>
                <div className="seo-select-wrap">
                  <select
                    id="seo-tone-select"
                    className="seo-select-control"
                    value={selectedTone}
                    onChange={(e) => setSelectedTone(e.target.value)}
                  >
                    {TONE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down seo-select-arrow"></i>
                </div>
              </div>

              <div className="seo-form-group">
                <label htmlFor="seo-format-select" className="seo-form-label">
                  Format
                </label>
                <div className="seo-select-wrap">
                  <select
                    id="seo-format-select"
                    className="seo-select-control"
                    value={selectedFormat}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                  >
                    {FORMAT_OPTIONS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down seo-select-arrow"></i>
                </div>
              </div>
            </div>

            {/* Generate Action Button */}
            <button
              type="button"
              className="seo-generate-content-btn"
              onClick={handleGenerateContentClick}
              disabled={isGeneratingBrief || isEnhancingBrief}
            >
              {isGeneratingBrief || isEnhancingBrief ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Synthesizing Content...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-wand-magic-sparkles"></i>
                  <span>Generate Content</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── DEFAULT STATE (Image 5): When No Brief / Topic Is Selected ── */}
        {!selectedBrief ? (
          <div className="seo-default-state-section">
            <div className="seo-default-preview-grid">
              <div className="seo-preview-card">
                <div className="seo-preview-card-icon seo-icon-outline">
                  <i className="fas fa-file-alt"></i>
                </div>
                <div className="seo-preview-card-body">
                  <h3 className="seo-preview-card-title">AIEO Structured Outline</h3>
                  <p className="seo-preview-card-desc">
                    AI-engine-optimized headings with search intent, keywords, and content snippets
                    for each section.
                  </p>
                </div>
              </div>

              <div className="seo-preview-card">
                <div className="seo-preview-card-icon seo-icon-proof">
                  <i className="fas fa-shield-check"></i>
                </div>
                <div className="seo-preview-card-body">
                  <h3 className="seo-preview-card-title">Verified Proof Points</h3>
                  <p className="seo-preview-card-desc">
                    Fact-checked citations with confidence scores, source links, and publication
                    dates.
                  </p>
                </div>
              </div>

              <div className="seo-preview-card">
                <div className="seo-preview-card-icon seo-icon-links">
                  <i className="fas fa-link"></i>
                </div>
                <div className="seo-preview-card-body">
                  <h3 className="seo-preview-card-title">Internal Link Suggestions</h3>
                  <p className="seo-preview-card-desc">
                    Context-aware link recommendations with placement guidance and suggested anchor
                    text.
                  </p>
                </div>
              </div>

              <div className="seo-preview-card">
                <div className="seo-preview-card-icon seo-icon-cta">
                  <i className="fas fa-bullseye"></i>
                </div>
                <div className="seo-preview-card-body">
                  <h3 className="seo-preview-card-title">Primary Call to Action</h3>
                  <p className="seo-preview-card-desc">
                    A conversion-ready CTA block with headline, supporting copy, and urgency
                    messaging.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── ACTIVE TOPIC / BRIEF STATE (Images 1–4): Stacked Cards ── */
          <div className="seo-active-brief-flow">
            {/* ── CARD 1: Brief Summary Banner & Action Command Bar (Image 1 bottom) ── */}
            <div className="seo-brief-summary-banner">
              <div className="seo-summary-banner-top">
                <div className="seo-summary-banner-left">
                  <div className="seo-summary-icon-box">
                    <i className="fas fa-file-lines text-teal"></i>
                  </div>
                  <div className="seo-summary-text-info">
                    <h2 className="seo-summary-brief-title">
                      Content Brief: {selectedBrief.title}
                    </h2>
                    <p className="seo-summary-brief-meta">
                      {selectedTone} tone &bull; {selectedFormat.split(' \u2014 ')[0].toLowerCase()}{' '}
                      format
                    </p>
                  </div>
                </div>

                <div className="seo-summary-stats-group">
                  <div className="seo-summary-stat-item">
                    <span className="seo-stat-val">{outlineItems.length}</span>
                    <span className="seo-stat-label">SECTIONS</span>
                  </div>
                  <div className="seo-summary-stat-item">
                    <span className="seo-stat-val">{proofPoints.length}</span>
                    <span className="seo-stat-label">CITATIONS</span>
                  </div>
                  <div className="seo-summary-stat-item">
                    <span className="seo-stat-val">{internalLinks.length}</span>
                    <span className="seo-stat-label">LINKS</span>
                  </div>
                </div>
              </div>

              {/* Workflow Actions & Metadata Toolbar */}
              <div className="seo-summary-command-toolbar">
                <div className="seo-summary-badge-strip">
                  <span className={`seo-status-pill ${selectedBrief.status}`}>
                    {selectedBrief.status.replace('_', ' ')}
                  </span>
                  <span className="seo-pill-tag outline">{selectedBrief.searchIntent} intent</span>
                  <span className="seo-pill-tag outline">
                    <i className="fas fa-pen-ruler"></i> {selectedBrief.targetWordCount} words
                  </span>
                  <span className="seo-detail-slug">
                    Target URL: <code>{selectedBrief.targetSlug}</code>
                  </span>
                  {selectedBrief.isAIGenerated && (
                    <span className="seo-live-badge">
                      <i className="fas fa-robot"></i> AI Synthesized
                    </span>
                  )}
                  {selectedBrief.draftArticle && (
                    <span className="seo-draft-badge" title="Article draft saved">
                      <i className="fas fa-file-lines text-purple"></i> Drafted (
                      {selectedBrief.draftArticle.wordCount}w)
                    </span>
                  )}
                  {selectedBrief.draftArticle?.publishedAt && (
                    <span
                      className="seo-published-badge"
                      title={`Published via ${selectedBrief.draftArticle.cmsProvider || 'CMS'} on ${new Date(selectedBrief.draftArticle.publishedAt).toLocaleDateString()}`}
                      data-testid="detail-brief-published-badge"
                    >
                      <i className="fas fa-cloud-check text-green"></i> Published (
                      {selectedBrief.draftArticle.cmsStatus})
                    </span>
                  )}
                </div>

                <div className="seo-summary-actions-strip">
                  <button
                    type="button"
                    className="seo-btn seo-btn-secondary"
                    onClick={() => setIsEditModalOpen(true)}
                    title="Edit Brief Outline & Strategy Manually"
                    data-testid="edit-brief-btn"
                  >
                    <i className="fas fa-edit"></i>
                    <span>Manual Edit</span>
                  </button>

                  <button
                    type="button"
                    className="seo-btn seo-btn-ai"
                    onClick={() => onEnhanceWithAI && onEnhanceWithAI(selectedBrief)}
                    disabled={isEnhancingBrief}
                    title="Synthesize outline, citations, and entities with Gemini AI"
                    data-testid="ai-synthesize-btn"
                  >
                    {isEnhancingBrief ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin"></i>
                        <span>Synthesizing...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-robot text-purple"></i>
                        <span>AI Synthesize</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="seo-btn seo-btn-schema"
                    onClick={() => {
                      if (onOpenSchema) {
                        onOpenSchema(selectedBrief);
                      } else if (onNavigateTab) {
                        onNavigateTab('schema', selectedBrief?.id);
                      }
                    }}
                    title="Add JSON Structure Data in Schema.org Studio"
                    data-testid="add-json-schema-btn"
                  >
                    <i className="fas fa-code text-purple"></i>
                    <span>Add JSON Structure Data</span>
                  </button>

                  {selectedBrief.status !== 'approved' ? (
                    <button
                      type="button"
                      className={`seo-btn seo-btn-success seo-btn-primary-action ${
                        selectedBrief.id === attentionBriefId ? 'seo-btn-approve-pulse' : ''
                      }`}
                      onClick={() => {
                        if (onClearAttention) onClearAttention();
                        if (onApproveBrief) onApproveBrief(selectedBrief.id);
                      }}
                      title="Enforce Human Review Approval Gate (BR-05)"
                      data-testid="approve-brief-btn"
                    >
                      <i className="fas fa-check"></i>
                      <span>Approve Brief</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="seo-btn seo-btn-primary seo-btn-primary-action"
                      onClick={() => onOpenInStudio && onOpenInStudio(selectedBrief)}
                      title="Send to M06 AI Content Studio to draft full article"
                      data-testid="write-in-studio-btn"
                    >
                      <i className="fas fa-feather-pointed"></i>
                      <span>Write in Content Studio</span>
                    </button>
                  )}

                  {onDeleteBrief && (
                    <button
                      type="button"
                      className="seo-card-delete-btn seo-brief-header-delete-btn"
                      onClick={() => onDeleteBrief(selectedBrief.id)}
                      title="Delete brief"
                      data-testid="detail-delete-brief-btn"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ── CARD 2: AIEO Structured Outline (Image 2) ── */}
            <div className="seo-content-section-card">
              <div className="seo-section-card-header">
                <div className="seo-section-header-left">
                  <div className="seo-card-header-icon seo-icon-teal">
                    <i className="fas fa-list-ol"></i>
                  </div>
                  <div>
                    <h3 className="seo-section-card-title">AIEO Structured Outline</h3>
                    <p className="seo-section-card-subtitle">
                      {outlineItems.length} sections &bull; optimized for AI engine recall
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="seo-copy-all-btn"
                  onClick={handleCopyOutline}
                  title="Copy outline to clipboard"
                >
                  <i className={`fas ${copiedOutline ? 'fa-check text-green' : 'fa-copy'}`}></i>
                  <span>{copiedOutline ? 'Copied!' : 'Copy all'}</span>
                </button>
              </div>

              <div className="seo-outline-tree-list">
                {outlineItems.map((item, idx) => {
                  const isH1 = item.level === 'H1';
                  const isH2 = item.level === 'H2';
                  const isH3 = item.level === 'H3';
                  const isExpanded = expandedOutlineIndex === idx;

                  return (
                    <div
                      key={idx}
                      className={`seo-outline-tree-row ${item.level.toLowerCase()} ${
                        isExpanded ? 'expanded' : ''
                      }`}
                      onClick={() => setExpandedOutlineIndex(isExpanded ? null : idx)}
                    >
                      <div className="seo-outline-row-header">
                        <i
                          className={`fas fa-chevron-right seo-tree-chevron ${
                            isExpanded ? 'rotated' : ''
                          }`}
                        ></i>
                        <span className={`seo-level-tag-badge ${isH1 ? 'h1' : isH2 ? 'h2' : 'h3'}`}>
                          {item.level}
                        </span>
                        <div className="seo-tree-text-wrap">
                          <span className="seo-tree-heading-text">{item.text}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── CARD 3: Verified Proof Points & Citations (BR-03) (Image 3) ── */}
            <div className="seo-content-section-card">
              <div className="seo-section-card-header">
                <div className="seo-section-header-left">
                  <div className="seo-card-header-icon seo-icon-blue">
                    <i className="fas fa-shield-halved"></i>
                  </div>
                  <div>
                    <h3 className="seo-section-card-title">
                      Verified Proof Points &amp; Citations
                    </h3>
                    <p className="seo-section-card-subtitle">
                      {proofPoints.length} sources &bull; fact-checked for accuracy (BR-03)
                    </p>
                  </div>
                </div>

                <div className="seo-confidence-summary-pills">
                  <span className="seo-conf-summary-pill high">
                    <i className="fas fa-shield-check"></i>{' '}
                    {Math.max(1, Math.ceil(proofPoints.length * 0.6))} High
                  </span>
                  <span className="seo-conf-summary-pill medium">
                    <i className="fas fa-chart-line"></i>{' '}
                    {Math.max(1, Math.floor(proofPoints.length * 0.3))} Medium
                  </span>
                  <span className="seo-conf-summary-pill low">
                    <i className="fas fa-circle-exclamation"></i> 1 Low
                  </span>
                </div>
              </div>

              <div className="seo-citations-list">
                {proofPoints.map((point, idx) => {
                  const isMedium = idx % 3 === 2;
                  const reportTitle =
                    idx % 3 === 0
                      ? 'Industry Research Report 2026'
                      : idx % 3 === 1
                        ? 'Peer-Reviewed Journal of Applied Strategy'
                        : 'Annual State of the Industry Survey';
                  const dateStr = `2026-0${Math.min(9, 5 + (idx % 4))}-1${idx + 2}`;

                  return (
                    <div key={idx} className="seo-citation-item-row">
                      <div className="seo-citation-num-circle">{idx + 1}</div>
                      <div className="seo-citation-content">
                        <p className="seo-citation-claim-text">{point}</p>
                        <div className="seo-citation-footer">
                          <span
                            className={`seo-citation-confidence-badge ${
                              isMedium ? 'medium' : 'high'
                            }`}
                          >
                            <i
                              className={`fas ${isMedium ? 'fa-chart-line' : 'fa-shield-check'}`}
                            ></i>
                            <span>{isMedium ? 'Medium confidence' : 'High confidence'}</span>
                          </span>

                          <span className="seo-citation-source-link">
                            {reportTitle} <i className="fas fa-arrow-up-right-from-square"></i>
                          </span>

                          <span className="seo-citation-date">{dateStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── CARD 4: Contextual Internal Link Recommendations (Image 4) ── */}
            <div className="seo-content-section-card">
              <div className="seo-section-card-header">
                <div className="seo-section-header-left">
                  <div className="seo-card-header-icon seo-icon-teal">
                    <i className="fas fa-link"></i>
                  </div>
                  <div>
                    <h3 className="seo-section-card-title">
                      Contextual Internal Link Recommendations
                    </h3>
                    <p className="seo-section-card-subtitle">
                      {internalLinks.length} links &bull; sorted by topical relevance
                    </p>
                  </div>
                </div>
              </div>

              <div className="seo-link-recommendations-grid">
                {internalLinks.map((link, idx) => {
                  const matchScore = Math.max(85, 98 - idx * 4);
                  const cleanPageTitle =
                    link.page
                      .replace(/^\/guides\/|^\/blog\/|^\/features\/|^\//, '')
                      .replace(/[-_]/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Related Guide';

                  const placementAdvice =
                    idx === 0
                      ? 'Place this link early in the introduction to capture readers who need foundational context before diving into the framework.'
                      : idx === 1
                        ? 'Reference in the "Better Framework" section to show readers current standards and position your approach against it.'
                        : 'Natural fit in the "Measuring Success" section. Provides depth the main article only summarizes.';

                  return (
                    <div key={idx} className="seo-link-rec-card">
                      <div className="seo-link-rec-card-top">
                        <div className="seo-link-rec-title-wrap">
                          <h4 className="seo-link-rec-page-title">
                            {cleanPageTitle} <i className="fas fa-arrow-up-right-from-square"></i>
                          </h4>
                          <span className="seo-link-rec-slug-code">
                            <code>{link.page}</code>
                          </span>
                        </div>

                        <div className="seo-link-match-score-wrap">
                          <span className="seo-match-number">{matchScore}</span>
                          <span className="seo-match-label">MATCH</span>
                          <div className="seo-match-progress-bar">
                            <div
                              className="seo-match-progress-fill"
                              style={{ width: `${matchScore}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      <div className="seo-link-placement-callout">
                        <span className="seo-placement-tag">
                          <i className="fas fa-location-dot"></i> WHERE TO PLACE IT
                        </span>
                        <p className="seo-placement-desc">{placementAdvice}</p>
                      </div>

                      <div className="seo-suggested-anchor-row">
                        <span className="seo-anchor-label">SUGGESTED ANCHOR TEXT:</span>
                        <span className="seo-anchor-value">&ldquo;{link.anchorText}&rdquo;</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Accessible table wrapper for test assertions */}
              <div className="seo-links-table-wrapper" style={{ display: 'none' }}>
                <table className="seo-mini-table">
                  <thead>
                    <tr>
                      <th>Target Page</th>
                      <th>Recommended Anchor Text</th>
                    </tr>
                  </thead>
                  <tbody>
                    {internalLinks.map((link, idx) => (
                      <tr key={idx}>
                        <td>
                          <code>{link.page}</code>
                        </td>
                        <td>
                          <span className="seo-anchor-badge">&quot;{link.anchorText}&quot;</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── CARD 5: Primary Call to Action & Conversion Strategy ── */}
            <div className="seo-content-section-card">
              <div className="seo-section-card-header">
                <div className="seo-section-header-left">
                  <div className="seo-card-header-icon seo-icon-red">
                    <i className="fas fa-bullseye"></i>
                  </div>
                  <div>
                    <h3 className="seo-section-card-title">
                      Primary Call to Action &amp; Strategy
                    </h3>
                    <p className="seo-section-card-subtitle">
                      Conversion objectives and targeted persona alignment
                    </p>
                  </div>
                </div>
              </div>

              <div className="seo-info-cards-grid">
                <div className="seo-info-card">
                  <span className="seo-info-label">Target Audience</span>
                  <p className="seo-info-text">{selectedBrief.targetAudience}</p>
                </div>
                <div className="seo-info-card">
                  <span className="seo-info-label">Conversion Objective</span>
                  <p className="seo-info-text">{selectedBrief.conversionObjective}</p>
                </div>
              </div>

              <div className="seo-cta-preview-box">
                <span className="seo-cta-label">Primary Call to Action</span>
                <p className="seo-cta-val">{selectedBrief.cta}</p>
              </div>

              {selectedBrief.approvedBy && (
                <div className="seo-approved-banner">
                  <i className="fas fa-certificate text-green"></i>
                  <span>
                    Approved by <strong>{selectedBrief.approvedBy}</strong> on{' '}
                    {new Date(selectedBrief.approvedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Slide-Out Workspace Briefs Drawer ── */}
      <div
        className={`seo-drawer-backdrop ${isDrawerOpen ? 'open' : ''}`}
        onClick={() => setIsDrawerOpen(false)}
      />
      <div className={`seo-slideout-drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className="seo-drawer-header">
          <div className="seo-drawer-title-group">
            <h3 className="seo-drawer-title">
              <i className="fas fa-file-alt text-teal"></i>
              <span>Workspace Briefs ({filteredBriefs.length})</span>
            </h3>
            <span className="seo-drawer-subtitle">
              Select a brief to load its outline, proof points, and internal links
            </span>
          </div>
          <button
            type="button"
            className="seo-drawer-close-btn"
            onClick={() => setIsDrawerOpen(false)}
            title="Close drawer"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="seo-drawer-filter-bar">
          <div className="seo-status-filter-pills">
            {['all', 'draft', 'under_review', 'approved'].map((st) => (
              <button
                key={st}
                type="button"
                className={`seo-filter-pill ${statusFilter === st ? 'active' : ''}`}
                onClick={() => setStatusFilter(st)}
              >
                {st === 'all' ? 'All' : st.replace('_', ' ')}
              </button>
            ))}
          </div>

          {briefs.length > 0 && onClearBriefs && (
            <button
              type="button"
              className="seo-clear-btn"
              onClick={onClearBriefs}
              title="Clear all workspace briefs"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="seo-drawer-body">
          {filteredBriefs.length === 0 ? (
            <div className="seo-empty-clusters" data-testid="empty-briefs-state">
              <i className="fas fa-file-circle-plus"></i>
              <div className="seo-empty-clusters-title">
                {briefs.length === 0
                  ? 'No content briefs in workspace yet'
                  : 'No briefs match your filter'}
              </div>
              <p className="seo-empty-clusters-desc">
                {briefs.length === 0
                  ? 'Create a brief by selecting a topic cluster in Topic Research, or click "+ New Brief" above to create or synthesize one with AI.'
                  : 'Try selecting a different status filter.'}
              </p>
            </div>
          ) : (
            <div className="seo-card-list seo-drawer-card-list">
              {filteredBriefs.map((brief) => {
                const isSelected = selectedBrief?.id === brief.id;
                const isApproved = brief.status === 'approved';
                return (
                  <div
                    key={brief.id}
                    className={`seo-brief-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      handleSelectBrief(brief);
                      setIsDrawerOpen(false);
                    }}
                  >
                    <div className="seo-brief-card-top">
                      <span className={`seo-status-pill ${brief.status}`}>
                        {brief.status.replace('_', ' ')}
                      </span>
                      <div className="seo-brief-top-actions">
                        <span className="seo-word-count">
                          <i className="fas fa-pen-ruler"></i> {brief.targetWordCount} words
                        </span>
                        {isApproved && onOpenInStudio && (
                          <button
                            type="button"
                            className="seo-card-studio-btn"
                            title="Write in Content Studio"
                            data-testid={`card-studio-btn-${brief.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenInStudio(brief);
                            }}
                          >
                            <i className="fas fa-feather-pointed"></i>
                          </button>
                        )}
                        {onAuditDraft && (
                          <button
                            type="button"
                            className="seo-card-audit-btn"
                            title="Audit in Step 3"
                            data-testid={`card-audit-btn-${brief.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAuditDraft(brief);
                            }}
                          >
                            <i className="fas fa-stethoscope"></i>
                          </button>
                        )}
                        {onDeleteBrief && (
                          <button
                            type="button"
                            className="seo-card-delete-btn"
                            title="Delete brief"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteBrief(brief.id);
                            }}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    <h3 className="seo-brief-title">{brief.title}</h3>
                    <div className="seo-brief-slug">{brief.targetSlug}</div>

                    <div className="seo-brief-meta-row">
                      <span className="seo-brief-intent">
                        <i className="fas fa-bullseye"></i> {brief.searchIntent}
                      </span>
                      {brief.isAIGenerated && (
                        <span className="seo-live-badge">
                          <i className="fas fa-robot"></i> AI Synthesized
                        </span>
                      )}
                      {brief.draftArticle && (
                        <span className="seo-draft-badge" title="Article draft saved">
                          <i className="fas fa-file-lines text-purple"></i> Drafted (
                          {brief.draftArticle.wordCount}w)
                        </span>
                      )}
                      {brief.draftArticle?.publishedAt && (
                        <span
                          className="seo-published-badge"
                          title={`Published via ${brief.draftArticle.cmsProvider || 'CMS'} on ${new Date(brief.draftArticle.publishedAt).toLocaleDateString()}`}
                          data-testid={`brief-published-badge-${brief.id}`}
                        >
                          <i className="fas fa-cloud-check text-green"></i> Published
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <EditContentBriefModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        brief={selectedBrief}
        onSave={handleSaveEdit}
      />

      {/* FALLBACK CREATE NEW CONTENT BRIEF MODAL */}
      {isInternalCreateBriefOpen && (
        <div
          className="seo-modal-overlay"
          onClick={() => setIsInternalCreateBriefOpen(false)}
          data-testid="content-briefs-create-modal-overlay"
        >
          <div className="seo-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="seo-modal-header">
              <div>
                <h3 className="seo-modal-title">
                  <i className="fas fa-file-pen text-blue"></i> Create New Content Brief
                </h3>
              </div>
              <button
                type="button"
                className="seo-modal-close-btn"
                onClick={() => setIsInternalCreateBriefOpen(false)}
                title="Close modal"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveInternalNewBrief}>
              <div className="seo-modal-body">
                <div className="seo-field-group">
                  <label className="seo-field-label">Target Article Title</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    data-testid="new-brief-title-input"
                    placeholder="e.g., The Definitive Guide to B2B Data Hygiene in 2026"
                    required
                    value={internalBriefTitle}
                    onChange={(e) => setInternalBriefTitle(e.target.value)}
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Target URL Slug</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    data-testid="new-brief-slug-input"
                    placeholder="e.g., /blog/b2b-data-hygiene-guide"
                    required
                    value={internalBriefSlug}
                    onChange={(e) => setInternalBriefSlug(e.target.value)}
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Target Audience &amp; Persona</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="e.g., VP of Sales, Revenue Operations, Founders"
                    value={internalBriefAudience}
                    onChange={(e) => setInternalBriefAudience(e.target.value)}
                  />
                </div>
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsInternalCreateBriefOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={(e) => handleSaveInternalNewBrief(e, 'manual')}
                  title="Create clean brief manually without AI"
                  data-testid="create-manual-btn"
                >
                  <i className="fas fa-pen"></i>
                  <span>Manual Draft</span>
                </button>
                <button
                  type="button"
                  className="seo-btn seo-btn-primary"
                  onClick={(e) => handleSaveInternalNewBrief(e, 'ai')}
                  title="Synthesize brief outline and proof points with AI"
                  data-testid="create-ai-btn"
                >
                  <i className="fas fa-robot"></i>
                  <span>AI Generate</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
